import React, { useState, useEffect, useRef } from 'react';
import { getStatus, createCheckout, createPortal, changePlan } from '../api/billing';

const PLAN_CARDS = [
  {
    key: 'free',
    name: 'フリー',
    price: 0,
    priceLabel: '無料',
    limitLabel: '累計 100 枚まで（リセットなし）',
    features: [
      '顔認識による自動振り分け',
      '累計 100 枚まで振り分け（月次リセットなし）',
      '子供の顔登録（複数人）',
      'Google ドライブ連携',
    ],
    color: '#6b7280',
    accent: '#f3f4f6',
    badge: null,
  },
  {
    key: 'standard',
    name: 'スタンダード',
    price: 3300,
    priceLabel: '¥3,300 / 月（税込）',
    limitLabel: '月 1,000 枚まで（毎月リセット）',
    features: [
      '顔認識による自動振り分け',
      '月 1,000 枚まで振り分け（毎月リセット）',
      'Google ドライブ連携',
    ],
    color: '#7048D4',
    accent: '#f5f0ff',
    badge: null,
  },
  {
    key: 'premium',
    name: 'プレミアム',
    price: 9900,
    priceLabel: '¥9,900 / 月（税込）',
    limitLabel: '月 10,000 枚まで（毎月リセット）',
    features: [
      '顔認識による自動振り分け',
      '月 10,000 枚まで振り分け（毎月リセット）',
      'Google ドライブ連携',
      '優先サポート',
    ],
    color: '#f59e0b',
    accent: '#fffbeb',
    badge: 'おすすめ',
  },
];

const SUB_STATUS_INFO = {
  none:             { icon: '⭕', label: '未契約',        color: '#9ca3af' },
  trialing:         { icon: '🎁', label: 'トライアル中',  color: '#f59e0b' },
  active:           { icon: '✅', label: '契約中',        color: '#06a96e' },
  cancel_scheduled: { icon: '🔔', label: 'キャンセル予定', color: '#f59e0b' },
  past_due:         { icon: '⚠️', label: '支払遅延',      color: '#e0324b' },
  canceled:         { icon: '❌', label: 'キャンセル済',  color: '#9ca3af' },
  unpaid:           { icon: '🚫', label: '未払い',        color: '#e0324b' },
};

function UsageBar({ usage, limit, color }) {
  const pct = Math.min(100, Math.round((usage / limit) * 100));
  const barColor = pct >= 90 ? '#e0324b' : pct >= 70 ? '#f59e0b' : color;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666', marginBottom: 4 }}>
        <span>{usage.toLocaleString()} 枚使用</span>
        <span>上限 {limit.toLocaleString()} 枚</span>
      </div>
      <div style={{ background: '#e5e7eb', borderRadius: 99, height: 8, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: barColor, borderRadius: 99,
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// プラン変更確認ダイアログ
function ConfirmDialog({ fromPlan, toPlan, onConfirm, onCancel, loading }) {
  const from = PLAN_CARDS.find(p => p.key === fromPlan);
  const to   = PLAN_CARDS.find(p => p.key === toPlan);
  const isUpgrade = (to?.price ?? 0) > (from?.price ?? 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '32px 28px',
        maxWidth: 420, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>
          {isUpgrade ? '⬆️' : '⬇️'}
        </div>
        <h2 style={{ textAlign: 'center', fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
          プランを{isUpgrade ? 'アップグレード' : 'ダウングレード'}しますか？
        </h2>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 12, margin: '20px 0', fontSize: 15,
        }}>
          <span style={{ fontWeight: 700, color: from?.color }}>{from?.name}</span>
          <span style={{ color: '#aaa' }}>→</span>
          <span style={{ fontWeight: 700, color: to?.color }}>{to?.name}</span>
        </div>
        <div style={{
          background: '#f8f9fa', borderRadius: 12, padding: '12px 16px',
          fontSize: 13, color: '#555', marginBottom: 24, lineHeight: 1.6,
        }}>
          {isUpgrade
            ? '✨ 即時反映されます。差額は日割りで請求されます。'
            : '📅 ダウングレードは即時反映されます。差額は次回請求から適用されます。'}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onCancel}
            disabled={loading}
          >
            キャンセル
          </button>
          <button
            className="btn btn-primary"
            style={{ flex: 1, background: to?.color, borderColor: to?.color }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading
              ? <><span className="spinner" />処理中…</>
              : '変更する'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage({ embedded = false }) {
  const [billing, setBilling]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState('');
  const [error, setError]                   = useState('');
  const [successMsg, setSuccessMsg]         = useState('');
  const [confirmTarget, setConfirmTarget]   = useState(null); // プラン変更確認対象
  const [polling, setPolling]               = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  const fetchBilling = async () => {
    try {
      const { data } = await getStatus();
      setBilling(data);
    } catch {
      setError('課金情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBilling(); }, []);

  useEffect(() => {
    if (window.location.hash === '#billing-success') {
      window.location.hash = '#billing';
      setSuccessMsg('🎉 サブスクリプションが開始されました！');
      fetchBilling();
    }
    if (window.location.hash === '#billing-cancel') {
      window.location.hash = '#billing';
    }
  }, []);

  // ポーリング開始（汎用）
  // mode: 'checkout' | 'upgrade' | 'portal'
  const startPolling = (currentPlan, currentStatus, mode) => {
    clearInterval(pollRef.current);
    setPolling(true);
    pollCountRef.current = 0;
    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      try {
        const { data } = await getStatus();
        const planChanged   = data.plan !== currentPlan;
        const statusChanged = data.subscription_status !== currentStatus;
        let changed = false;
        let msg = '';

        if (mode === 'checkout' && planChanged && data.plan !== 'free') {
          msg = `🎉 ${data.plan === 'standard' ? 'スタンダード' : 'プレミアム'}プランへの変更が完了しました！`;
          changed = true;
        } else if (mode === 'upgrade' && planChanged) {
          const name = PLAN_CARDS.find(p => p.key === data.plan)?.name ?? data.plan;
          msg = `🎉 ${name}プランへのアップグレードが完了しました！`;
          changed = true;
        } else if (mode === 'portal' && statusChanged) {
          if (data.subscription_status === 'cancel_scheduled') {
            msg = '🔔 解約予約が完了しました。現在の期間終了まで引き続きご利用いただけます。';
          } else if (data.subscription_status === 'canceled') {
            msg = '❌ サブスクリプションが解約されました。';
          } else if (statusChanged || planChanged) {
            msg = '✅ プランの変更が反映されました。';
          }
          changed = !!msg;
        }

        if (changed) {
          setBilling(data);
          setSuccessMsg(msg);
          clearInterval(pollRef.current);
          setPolling(false);
        }
      } catch {}
      // 2分（40回×3秒）でタイムアウト
      if (pollCountRef.current >= 40) {
        clearInterval(pollRef.current);
        setPolling(false);
        fetchBilling();
      }
    }, 3000);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  // 新規チェックアウト（サブスク未加入）
  const handleCheckout = async (plan) => {
    setActionLoading(plan); setError('');
    try {
      const { data } = await createCheckout(plan);
      window.open(data.checkout_url, '_blank');
      // 支払い完了後の自動反映のためポーリング開始
      startPolling(billing?.plan ?? 'free', billing?.subscription_status ?? 'none', 'checkout');
    } catch (err) {
      setError(err.response?.data?.detail ?? 'チェックアウトの開始に失敗しました');
    } finally {
      setActionLoading('');
    }
  };

  // プラン変更確認ダイアログを開く
  const handleChangePlanClick = (plan) => {
    setConfirmTarget(plan);
    setError('');
    setSuccessMsg('');
  };

  // プラン変更実行
  const handleChangePlanConfirm = async () => {
    if (!confirmTarget) return;
    const prevPlan   = billing?.plan ?? 'free';
    const prevStatus = billing?.subscription_status ?? 'none';
    setActionLoading(confirmTarget); setError('');
    try {
      await changePlan(confirmTarget);
      setConfirmTarget(null);
      // Webhook反映を待つためポーリング開始
      startPolling(prevPlan, prevStatus, 'upgrade');
    } catch (err) {
      setError(err.response?.data?.detail ?? 'プランの変更に失敗しました');
      setConfirmTarget(null);
    } finally {
      setActionLoading('');
    }
  };

  const handlePortal = async () => {
    const prevPlan   = billing?.plan ?? 'free';
    const prevStatus = billing?.subscription_status ?? 'none';
    setActionLoading('portal'); setError('');
    try {
      const { data } = await createPortal();
      window.open(data.portal_url, '_blank');
      // ポータルでの操作（解約など）を自動検知
      startPolling(prevPlan, prevStatus, 'portal');
    } catch (err) {
      setError(err.response?.data?.detail ?? 'ポータルへのアクセスに失敗しました');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
      </div>
    );
  }

  const currentPlan     = billing?.plan ?? 'free';
  const subStatus       = billing?.subscription_status ?? 'none';
  const subInfo         = SUB_STATUS_INFO[subStatus] ?? SUB_STATUS_INFO.none;
  const periodEnd       = billing?.subscription_period_end
    ? new Date(billing.subscription_period_end).toLocaleDateString('ja-JP')
    : null;
  const hasSubscription = ['trialing', 'active', 'cancel_scheduled', 'past_due'].includes(subStatus) && billing?.stripe_customer_id;
  const planDetail      = billing?.plan_detail ?? { limit: 100, limit_type: 'cumulative' };
  const currentUsage    = billing?.current_usage ?? 0;
  const remaining       = billing?.remaining ?? 0;

  return (
    <div className={embedded ? undefined : 'page'}>
      {confirmTarget && (
        <ConfirmDialog
          fromPlan={currentPlan}
          toPlan={confirmTarget}
          loading={actionLoading === confirmTarget}
          onConfirm={handleChangePlanConfirm}
          onCancel={() => setConfirmTarget(null)}
        />
      )}

      {!embedded && (
        <div className="page-header animate-in">
          <h1 className="page-title">料金プラン</h1>
          <p className="page-subtitle">えがおフォルダの利用プランを選択・管理します</p>
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'} style={{ maxWidth: 860 }}>

        {error      && <div className="msg msg-error   animate-in" style={{ marginBottom: 16 }}>{error}</div>}
        {successMsg && <div className="msg msg-success animate-in" style={{ marginBottom: 16 }}>{successMsg}</div>}
        {polling    && <div className="msg animate-in" style={{ marginBottom: 16, background: '#f0f4ff', color: '#5a5fc8', border: '1px solid #c7d0f8' }}>
          <span className="spinner spinner-dark" style={{ marginRight: 8 }} />支払い完了を確認中…（自動で更新されます）
        </div>}

        {/* ── 現在の利用状況 ── */}
        <div style={{
          background: '#fff', borderRadius: 20, padding: '24px 28px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
          border: '1px solid var(--border)', marginBottom: 32,
        }} className="animate-in">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>現在のプラン</div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>
                {PLAN_CARDS.find(p => p.key === currentPlan)?.name ?? currentPlan}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 20 }}>{subInfo.icon}</span>
              <span style={{ fontWeight: 700, color: subInfo.color, fontSize: 14 }}>{subInfo.label}</span>
              {periodEnd && (
                <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>
                  （{subStatus === 'trialing' ? 'トライアル終了' : '次回更新'}: {periodEnd}）
                </span>
              )}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>
              振り分け使用量
              <span style={{ marginLeft: 6, color: '#bbb' }}>
                （{planDetail.limit_type === 'cumulative' ? '累計' : '今月'}）
              </span>
            </div>
            <UsageBar
              usage={currentUsage}
              limit={planDetail.limit}
              color={PLAN_CARDS.find(p => p.key === currentPlan)?.color ?? '#7048D4'}
            />
            <div style={{ fontSize: 12, color: remaining === 0 ? '#e0324b' : '#666', marginTop: 6, textAlign: 'right' }}>
              残り <strong>{remaining.toLocaleString()}</strong> 枚
            </div>
          </div>

          {hasSubscription && (
            <div style={{ marginTop: 16 }}>
              <button
                className="btn btn-secondary"
                style={{ padding: '8px 20px', fontSize: 13 }}
                disabled={!!actionLoading}
                onClick={handlePortal}
              >
                {actionLoading === 'portal'
                  ? <><span className="spinner" />処理中…</>
                  : '💳 支払い・解約を管理'}
              </button>
            </div>
          )}
        </div>

        {/* ── プランカード ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
          {PLAN_CARDS.map((plan, idx) => {
            const isCurrent = plan.key === currentPlan;
            const isPaid    = plan.key !== 'free';

            // ボタンの種類を判定
            // ・未契約 → 新規チェックアウト
            // ・契約済み → プラン変更（changePlan API）
            const isChangeTarget = hasSubscription && !isCurrent && isPaid;
            const isNewCheckout  = !hasSubscription && !isCurrent && isPaid;

            return (
              <div
                key={plan.key}
                className={`animate-in-delay-${idx}`}
                style={{
                  background: isCurrent ? plan.accent : '#fff',
                  borderRadius: 20,
                  padding: '28px 24px',
                  border: isCurrent
                    ? `2px solid ${plan.color}`
                    : '1px solid var(--border)',
                  position: 'relative',
                  boxShadow: isCurrent
                    ? `0 6px 24px ${plan.color}22`
                    : '0 2px 12px rgba(0,0,0,0.06)',
                  display: 'flex', flexDirection: 'column',
                  transition: 'box-shadow 0.2s',
                }}
              >
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: plan.color, color: '#fff',
                    padding: '2px 14px', borderRadius: 99,
                    fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap',
                  }}>
                    {plan.badge}
                  </div>
                )}

                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: 16, right: 16,
                    background: plan.color, color: '#fff',
                    padding: '2px 10px', borderRadius: 99,
                    fontSize: 10, fontWeight: 800,
                  }}>
                    現在のプラン
                  </div>
                )}

                <div style={{ fontWeight: 800, fontSize: 18, color: plan.color, marginBottom: 6 }}>
                  {plan.name}
                </div>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#1a1a2e', marginBottom: 2 }}>
                  {plan.priceLabel}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
                  {plan.limitLabel}
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#444', alignItems: 'flex-start' }}>
                      <span style={{ color: plan.color, fontWeight: 800, flexShrink: 0 }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* 新規チェックアウト（サブスク未加入） */}
                {isNewCheckout && (
                  <button
                    className="btn btn-primary"
                    style={{ padding: '10px 0', fontSize: 13, width: '100%', background: plan.color, borderColor: plan.color }}
                    disabled={!!actionLoading}
                    onClick={() => handleCheckout(plan.key)}
                  >
                    {actionLoading === plan.key
                      ? <><span className="spinner" />処理中…</>
                      : `🚀 ${plan.name}を開始`}
                  </button>
                )}

                {/* プラン変更（サブスク加入済み） */}
                {isChangeTarget && (() => {
                  const currentPrice = PLAN_CARDS.find(p => p.key === currentPlan)?.price ?? 0;
                  const isUpgrade = plan.price > currentPrice;
                  return (
                    <button
                      className="btn btn-primary"
                      style={{ padding: '10px 0', fontSize: 13, width: '100%', background: plan.color, borderColor: plan.color }}
                      disabled={!!actionLoading}
                      onClick={() => handleChangePlanClick(plan.key)}
                    >
                      {actionLoading === plan.key
                        ? <><span className="spinner" />処理中…</>
                        : isUpgrade
                          ? `⬆️ ${plan.name}にアップグレード`
                          : `⬇️ ${plan.name}にダウングレード`}
                    </button>
                  );
                })()}

                {/* 現在のプラン */}
                {isCurrent && (
                  <div style={{
                    textAlign: 'center', fontSize: 13, color: plan.color,
                    fontWeight: 700, padding: '10px 0',
                    border: `1px solid ${plan.color}`, borderRadius: 10,
                  }}>
                    ご利用中
                  </div>
                )}

                {/* フリープランに戻る案内 */}
                {!isCurrent && !isPaid && (
                  <div style={{
                    textAlign: 'center', fontSize: 12, color: '#aaa',
                    padding: '10px 0',
                  }}>
                    有料プランをキャンセルすると自動的に戻ります
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 注記 */}
        {!hasSubscription && (
          <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginTop: 24 }}>
            ※ フリープランからいつでもアップグレードできます。クレジットカードが必要です。
          </p>
        )}
      </div>
    </div>
  );
}
